import type { FonteDaFoto } from "@foto/shared";
import { LupaDeAmpliar } from "../../components/Ampliar.js";
import { cx } from "../../components/ui.js";
import { estiloDaGrade, imagensNaVersao, rotuloDaLupa } from "./ampliar-fotos.js";
import type { FotoNaBandeja } from "./folhas.js";

/**
 * A grade de fotos publicadas, a mesma na tela de escolha dos Marcadores e na bandeja da Impressão:
 * a miniatura na versão pedida, o nome embaixo e a lupa no canto.
 *
 * Tocar na miniatura **escolhe** (ou coloca na folha, conforme a tela); a lupa **amplia sem escolher**.
 * A lupa é irmã da miniatura, e não filha: um botão dentro de outro é HTML inválido, e o toque
 * subiria e escolheria a foto.
 *
 * As colunas são as que couberem (`auto-fill`), nenhuma abaixo de `minimo`: em vez de 3, 4 ou 5
 * colunas fixas por largura de tela, a miniatura cresce com o espaço.
 */
export function GradeDeFotos({
  fotos,
  versao,
  minimo,
  rotuloDoToque,
  aoEscolher,
  aoAmpliar,
  escolhida,
  usos,
}: {
  fotos: readonly FotoNaBandeja[];
  /** A versão que as miniaturas mostram. */
  versao: FonteDaFoto;
  /** A largura mínima de uma coluna, em CSS (`9rem`). */
  minimo: string;
  /** O nome que o toque na miniatura leva para o leitor de tela. */
  rotuloDoToque: (foto: FotoNaBandeja) => string;
  aoEscolher: (foto: FotoNaBandeja) => void;
  aoAmpliar: (foto: FotoNaBandeja) => void;
  /** A foto já escolhida, realçada. Sem ela (a Impressão coloca várias), nada fica marcado. */
  escolhida?: number | null | undefined;
  /** Quantas vezes cada foto já está na folha: vira a etiqueta "2×" no canto. */
  usos?: ReadonlyMap<number, number> | undefined;
}) {
  return (
    <ul className="grid gap-2" style={estiloDaGrade(minimo)}>
      {fotos.map((f) => {
        const marcada = escolhida === f.id;
        const vezes = usos?.get(f.id);
        return (
          <li key={f.id} className="relative">
            <button
              type="button"
              onClick={() => aoEscolher(f)}
              aria-pressed={escolhida === undefined ? undefined : marcada}
              aria-label={rotuloDoToque(f)}
              title={f.nome}
              className="group block w-full text-left"
            >
              <span
                className={cx(
                  "relative block aspect-[3/4] overflow-hidden rounded-peca border-2 bg-superficie",
                  marcada ? "border-brasa" : "border-transparent group-hover:border-borda-forte",
                )}
              >
                <img
                  src={imagensNaVersao(f, versao).mini}
                  alt=""
                  loading="lazy"
                  className="size-full object-cover"
                />
                {vezes !== undefined && (
                  <span
                    aria-hidden
                    className="absolute top-1.5 left-1.5 rounded-full bg-brasa px-2 py-0.5 text-[0.6875rem] font-medium text-fundo"
                  >
                    {vezes}×
                  </span>
                )}
              </span>
              <span className="mt-1 block truncate text-[0.8125rem] text-texto-suave">
                {f.nome}
              </span>
            </button>
            <LupaDeAmpliar
              rotulo={rotuloDaLupa(f.nome)}
              aoClicar={() => aoAmpliar(f)}
              className="absolute top-0 right-0"
            />
          </li>
        );
      })}
    </ul>
  );
}
